import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import NewsletterSignup from "./components/NewsletterSignup";
import { Mail, Phone } from "lucide-react";

export default function Footer() {
  const { t } = useTranslation();
  
  return (
    <>
      {/* Wave Divider */}
      <div className="relative">
        <svg
          className="w-full h-16 md:h-24"
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z"
            className="fill-raya-bg"
          />
        </svg>
      </div>

      <footer className="bg-raya-bg text-raya-text-primary transition-colors duration-300">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            
            {/* Brand & Newsletter */}
            <div className="lg:col-span-1">
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                RayaElegance
              </h3>
              <p className="text-raya-text-secondary mb-4 leading-relaxed">
                {t('footer.description')}
              </p>
              <div className="flex items-center gap-2 text-raya-text-tertiary mb-6">
                <span className="text-2xl">🇪🇹</span>
                <span className="text-sm font-medium">Proudly Ethiopian</span>
              </div>
              <NewsletterSignup />
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-lg mb-6 text-raya-text-primary">{t('footer.quickLinks')}</h4>
              <ul className="space-y-3">
                {[
                  { to: "/products", label: t('navbar.products') },
                  { to: "/about", label: t('navbar.about') },
                  { to: "/contact", label: t('navbar.contact') },
                  { to: "/faq", label: t('navbar.support') },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-raya-text-secondary hover:text-raya-accent transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      <span className="text-yellow-600 group-hover:translate-x-1 transition-transform">▸</span>
                      <span className="relative">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-yellow-400 to-yellow-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Customer Service */}
            <div>
              <h4 className="font-semibold text-lg mb-6 text-raya-text-primary">{t('footer.support')}</h4>
              <ul className="space-y-3">
                {[
                  { to: "/privacy", label: t('navbar.privacy') },
                  { to: "/terms", label: t('navbar.terms') },
                  { to: "/shipping", label: t('navbar.shipping') },
                  { to: "/returns", label: t('returnPolicy.title') },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-raya-text-secondary hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 inline-flex items-center gap-2 group"
                    >
                      <span className="text-yellow-600 group-hover:translate-x-1 transition-transform">▸</span>
                      <span className="relative">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-yellow-400 to-yellow-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Contact Info */}
              <div className="mt-6 space-y-2 text-sm text-raya-text-tertiary">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@raya-elegance.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (843) 847-0158</span>
                </div>
              </div>
            </div>

            {/* Connect & Trust */}
            <div>
              <h4 className="font-semibold text-lg mb-6 text-raya-text-primary">{t('footer.followUs')}</h4>
              
              {/* Social Media */}
              <div className="flex gap-3 mb-8">
                {/* Facebook */}
                <a
                  href="https://facebook.com/rayaelegance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  title="Facebook"
                >
                  <div className="w-12 h-12 bg-white/50 dark:bg-white/5 backdrop-blur-xl border border-zinc-200 dark:border-white/10 rounded-xl flex items-center justify-center
                    hover:bg-gradient-to-br hover:from-blue-600 hover:to-blue-700
                    hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/30
                    transform hover:scale-110 hover:rotate-3 transition-all duration-300">
                    <svg className="w-6 h-6 text-raya-text-tertiary group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </div>
                </a>

                {/* Instagram */}
                <a
                  href="https://instagram.com/rayaelegance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  title="Instagram"
                >
                  <div className="w-12 h-12 bg-raya-card/50 backdrop-blur-xl border border-raya-border rounded-xl flex items-center justify-center
                    hover:bg-gradient-to-br hover:from-pink-600 hover:via-purple-600 hover:to-orange-500
                    hover:border-pink-500 hover:shadow-lg hover:shadow-pink-500/30
                    transform hover:scale-110 hover:rotate-3 transition-all duration-300">
                    <svg className="w-6 h-6 text-raya-text-tertiary group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="4" />
                      <circle cx="17.5" cy="6.5" r="1.5" />
                    </svg>
                  </div>
                </a>

                {/* Twitter/X */}
                <a
                  href="https://twitter.com/rayaelegance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  title="Twitter"
                >
                  <div className="w-12 h-12 bg-raya-card/50 backdrop-blur-xl border border-raya-border rounded-xl flex items-center justify-center
                    hover:bg-gradient-to-br hover:from-sky-500 hover:to-sky-600
                    hover:border-sky-500 hover:shadow-lg hover:shadow-sky-500/30
                    transform hover:scale-110 hover:rotate-3 transition-all duration-300">
                    <svg className="w-6 h-6 text-raya-text-tertiary group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                    </svg>
                  </div>
                </a>
              </div>

              {/* Payment Methods */}
              <div className="mb-6">
                <p className="text-sm text-raya-text-tertiary mb-3 font-medium">{t('footer.weAccept')}</p>
                <div className="flex gap-2 flex-wrap">
                  {['Visa', 'Mastercard', 'AmEx', 'Stripe'].map((method) => (
                    <div
                      key={method}
                      className="px-3 py-1.5 bg-raya-card/50 border border-raya-border rounded-lg text-xs text-raya-text-tertiary font-medium hover:bg-raya-accent-light transition"
                    >
                      {method}
                    </div>
                  ))}
                </div>
              </div>

              {/* Security Badge */}
              <div className="flex items-center gap-2 text-xs text-raya-text-tertiary">
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{t('footer.sslSecure')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-raya-border bg-raya-bg transition-colors duration-300">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-raya-text-tertiary">
              <p>© {new Date().getFullYear()} RayaElegance. {t('footer.rights')}</p>
              <div className="flex items-center gap-4">
                <span className="text-raya-text-tertiary">{t('footer.madeWith')}</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
